<template>
  <div class="text-[18px]  max-w-[800px] mx-auto mb-5 g--copy">
    <div class="flex items-center gap-x-4 gap-y-4 flex-wrap">
      <div class="w-full md:w-auto">
        <label class="block font-title text-[24px] font-medium text-gray-700 mb-1">Select your school</label>

        <select class="g--select" v-model="selectedSchool">
          <option value="" disabled>Select a school</option>
          <optgroup label="State">
            <option v-for="school in schools" :key="school.name">{{ school.name }}</option>
          </optgroup>
          <optgroup label="Independent">
            <option v-for="school in indSchools" :key="school.name">{{ school.name }}</option>
          </optgroup>
        </select>
      </div>
      <div class="w-full md:w-auto">
        <label class="block font-title text-[24px] font-medium text-gray-700 mb-1">Add your child's name</label>
        <input type="text" class="g--input" v-model="childName" placeholder=""/>
      </div>
      <div v-if="selectedSchool && childName" class="flex self-end w-full md:w-auto">
        <button @click="highlightText = !highlightText"
                class="bg-blue/90 text-white font-title text-[22px] uppercase rounded-md px-4 py-2">
          <span v-if="highlightText">Unhighlight</span>
          <span v-else>Highlight</span>
          personalised text
        </button>
      </div>
    </div>
    <div class="mt-4 border-b border-black/50 pb-4" v-if="!selectedSchool || !childName">
      Please select a school and enter your child's name above. No data is stored.
    </div>
    <div v-else class=" border-t border-black/50 pt-4 mt-8">


      <div class="mb-4 mt-4">
        <div class="block font-title text-[24px] font-medium text-gray-700 mb-1">Email subject</div>
        <div ref="emailSubject">
          FAO <span
            :class="{ 'bg-blue/90 text-white' : highlightText }">{{ selectedSchoolObject.headteacherFull }}</span>,
          smartphones at <span
            :class="{ 'bg-blue/90 text-white' : highlightText }">{{ selectedSchoolObject.name }}</span> for upcoming
          cohort.
        </div>
      </div>

      <div class="flex items-center gap-x-4">
        <button @click="copySubject()"
                class="bg-blue/90 text-white font-title text-[22px] uppercase rounded-md px-4 py-2">Copy
          email subject
        </button>
        <div v-if="subjectCopied">Copied!</div>
      </div>


      <div class="mt-8 block font-title text-[24px] font-medium text-gray-700 mb-1">Email body</div>
      <div ref="emailBody">
        <p>Dear <span :class="{ 'bg-blue/90 text-white' : highlightText }">{{
            selectedSchoolObject.headteacherFull
          }}</span>,
        </p>

        <p>I am delighted that my child <span
            :class="{ 'bg-blue/90 text-white' : highlightText }">{{ childName }}</span>
          has been allocated a place at <span
              :class="{ 'bg-blue/90 text-white' : highlightText }">{{ selectedSchoolObject.name }}</span> to
          start Year 7 in September.</p>


        <div v-if="selectedSchoolObject.fully">
          <p>I am writing to say a huge thank you for introducing the new feature / brick phone only policy. As a parent
            it is a
            huge relief to know that this school takes the issue so seriously and is so determined to protect our
            children from the many negative impacts of smartphones and smartwatches.</p>

          <p>I feel certain that this bold, gold star policy is going to have a hugely positive effect on <span
              :class="{ 'bg-blue/90 text-white' : highlightText }">{{ childName }}</span> and on
            their friends and peers – on their physical and mental wellbeing, as well as on their ability to learn and
            make the most of all that <span :class="{ 'bg-blue/90 text-white' : highlightText }">{{
                selectedSchoolObject.name
              }}</span> has to offer in the years ahead.</p>

          <p>We are grateful for the thoughtful and well-informed leadership you have shown on this issue. It reinforces
            our belief that this is the right school for our family.</p>

          <p>With sincere thanks,</p>

        </div>
        <div v-else>

          <p>I am writing about your policy on smartphones and to ask if you will be reviewing it
            before the new academic year, particularly in light of the latest government guidance
            that clarifies that smartphones have no place in schools?</p>
          <p v-if="selectedSchoolObject.pouches">
            I understand <span
              :class="{ 'bg-blue/90 text-white' : highlightText }">{{ selectedSchoolObject.name }}</span> is currently
            using
            pouches, which is a welcome first step. I am
            encouraged to see you taking the issue seriously and it was one of the reasons we
            chose this school. However, pouches do little to protect children on the way to and
            from school, and may in fact normalise having a smartphone, meaning all the risks
            associated with them will continue around school life.
          </p>

          <p><span :class="{ 'bg-blue/90 text-white' : highlightText }">{{ childName }}</span> does not have a
            smartphone
            and we will not be giving them one until they are older. It is hugely important to me that they
            have the time to build confidence, resilience and strong relationships, without the
            constant pull of a screen in their pocket. Smartphones have been linked
            unequivocally to cyberbullying, grooming, mental health problems, harmful content,
            lack of sleep and attention issues, and I am determined to protect them from this for as long as possible.
          </p>

          <p>I know more and more parents feel the same as I do and more children in {{ childName }}'s year group will
            be
            arriving at <span :class="{ 'bg-blue/90 text-white' : highlightText }">{{
                selectedSchoolObject.name
              }}</span>
            with brick phones than in previous years. I am wondering
            whether you have considered going fully smartphone free for this cohort of children?</p>
          <p>Not allowing smartphones to be brought to school at all for this year group would be the gold standard for
            safeguarding our children from the harms presented by smartphones. By limiting
            to brick phones only at school it will remove peer pressure and keep them safer for
            longer, minimising issues at home and on journeys to school.</p>

          <p>In the recent citywide parent survey on smartphones, which received 2,666 responses, an overwhelming 81% of parents said they would fully support their school going fully smartphone free. I am also aware that King's School has announced that it will be implementing a complete smartphone ban for the next year 7 cohort from this September.</p>

          <p>I would welcome hearing your thoughts on this issue.</p>

          <p>With kind regards,</p>
        </div>
      </div>

      <div class="flex items-center gap-x-4 mt-4">
        <button @click="copyBody()" class="bg-blue/90 text-white font-title text-[22px] uppercase rounded-md px-4 py-2">
          Copy
          email body
        </button>
        <div v-if="bodyCopied">Copied!</div>
      </div>
    </div>
  </div>
</template>

<script setup>
// import '@mapbox-gl/dist/mapbox-gl.css';

import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

const selectedSchool = ref('');
const childName = ref('');
const highlightText = ref(false);

const subjectCopied = ref(false);
const bodyCopied = ref(false);
const emailBody = ref(null);


let schools = [

  {
    name: 'BACA',
    headteacherFull: 'Jack Davies',
    headteacher: 'Jack'
  },
  {
    headteacher: 'Kate',
    headteacherFull: 'Kate Claydon',
    name: 'Blatchington Mill School'
  },

  {
    name: 'Cardinal Newman',
    pouches: true,
    headteacher: 'Claire',
    headteacherFull: 'Claire Jarman'
  },
  {
    name: 'Dorothy Stringer',
    pouches: true,
    headteacherFull: 'Matt Hillier',
    headteacher: 'Matt'
  },


  {
    name: 'Hove Park School',
    headteacher: 'Jim',
    headteacherFull: 'Jim Roberts'
  },
  {
    name: 'King\'s',
    headteacher: 'Sarah',
    headteacherFull: 'Sarah Price',
    fully: true
  },
  {
    name: 'Longhill',
    headteacher: 'Mrs Otulakowski',
    headteacherFull: 'Mrs Otulakowski',
  },
  {
    name: 'PACA',
    headteacher: 'Mark',
    headteacherFull: 'Mark Poston'
  },
  {
    name: 'Patcham High School',
    headteacher: 'John',
    headteacherFull: 'John McKee',
    pouches: true,
  },
  {
    name: 'Varndean',
    headteacher: 'Shelley',
    headteacherFull: 'Shelley Baker',
    pouches: false,
  },

];

let indSchools = [
  {
    name: 'Brighton Girls',
    headteacherFull: 'Melinda Shead',
    headteacher: 'Melinda Shead'
  },
  {
    name: 'Lancing Prep',
    headteacherFull: 'Kirsty Keep',
    headteacher: 'Kirsty Keep'
  },
  {
    name: 'Roedean',
    headteacherFull: 'Niamh Green',
    headteacher: 'Niamh Green'
  },
  {
    name: 'Shoreham College',
    headteacherFull: 'Sarah Bakhtiari',
    headteacher: 'Sarah Bakhtiari',
    pouches: true
  },
  {
    name: 'The Montessori Place',
    headteacherFull: 'Rob Gueterbock',
    headteacher: 'Rob Gueterbock'
  },


]

onMounted(() => {
  console.log('Component mounted');
  // Initialize with all schools
});

const emailSubject = ref(null);


// computed function to return selected school object
const selectedSchoolObject = computed(() => {

  let outSchool = schools.find(school => school.name === selectedSchool.value);
  if (!outSchool) {
    outSchool = indSchools.find(school => school.name === selectedSchool.value);
  }
  return outSchool;
});

const copySubject = async () => {
  await navigator.clipboard.writeText(emailSubject.value.innerText);
  subjectCopied.value = true;
  setTimeout(function() {
    subjectCopied.value = false;
  }, 2000);
}

const copyBody = async () => {
  await navigator.clipboard.writeText(emailBody.value.innerText);
  bodyCopied.value = true;
  setTimeout(function() {
    bodyCopied.value = false;
  }, 2000);
}

function strip(html) {
  let doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
}

</script>
